import React, { useState } from 'react';
import {
  ApButtonMain,
  ApButtonSecondary,
  ApInput,
} from '@alixpartners/ui-components';

import {
  ContentBlock,
  CostLabel,
  CostSectionWrapper,
  Description,
  InputButtonGroup,
  RightSection,
  RightSectionColumn,
  Title,
} from '../EnvironmentSetup.styles';
import { ExpenseDetailsModal } from '../ExpenseDetailsModal';
import CodeSnippet from '../../../../components/CodeSnippet';
import { IExpenseDetail } from '../../../../types';
import { getTotal } from '../../../../utils/common';

interface ICostSectionProps {
  expensesAlertLimit: number;
  expenseDetails: IExpenseDetail[];
  projectCodeName?: string;
  userName?: string;
  onSave: (state: { expensesAlertLimit: number }) => any;
}

export const CostSection = ({
  expensesAlertLimit: originalExpensesAlertLimit,
  expenseDetails,
  projectCodeName,
  userName,
  onSave,
}: ICostSectionProps) => {
  const [validationErrorText, setValidationErrorText] = useState('');
  const [expensesAlertLimit, setExpensesAlertLimit] = useState(
    originalExpensesAlertLimit || 0
  );
  const handleSave = () => {
    onSave({ expensesAlertLimit });
  };

  const saveDisabled =
    !validationErrorText && originalExpensesAlertLimit === expensesAlertLimit;

  const [isCostsModalOpen, setIsCostsModalOpen] = useState(false);

  const formatExpense = (value: string | number | undefined) => {
    if (!value) return value;

    return typeof value === 'number' ? `$ ${value.toFixed()}` : `$ ${value}`;
  };

  const expensesTotal = getTotal(
    expenseDetails as any
  ) as IExpenseDetail | null;

  return (
    <>
      <CostSectionWrapper animationIndex={1}>
        <Title>Cost</Title>
        <ContentBlock>
          <Description>General information about environment cost</Description>
          <RightSection>
            <RightSectionColumn>
              <CostLabel>Last month actual cost</CostLabel>
              <CodeSnippet>
                {expensesTotal &&
                  formatExpense(expensesTotal.previousMonthExpenses)}
              </CodeSnippet>
            </RightSectionColumn>
            <RightSectionColumn>
              <CostLabel>This month actual to date cost</CostLabel>
              <CodeSnippet>
                {expensesTotal &&
                  formatExpense(expensesTotal.currentMonthExpenses)}
              </CodeSnippet>
            </RightSectionColumn>
            <RightSectionColumn>
              <ApButtonSecondary onClick={() => setIsCostsModalOpen(true)}>
                Cost details
              </ApButtonSecondary>
            </RightSectionColumn>
          </RightSection>
        </ContentBlock>
        <ContentBlock>
          <Description>
            Let us know when you want the MD and all Alixpartners users to be
            informed by email when the spend hits this amount in one calendar
            month.
          </Description>
          <RightSection>
            <InputButtonGroup hasErrorMessage={!!validationErrorText}>
              <ApInput
                label="Azure billing warning limt"
                defaultValue={originalExpensesAlertLimit?.toString() || ''}
                symbol="$"
                errorMessage={validationErrorText}
                onChange={(value) => {
                  const val = Number(value);

                  if (val >= 5000) {
                    setValidationErrorText(
                      'You can not set a value higher than $5000 per month, please change.'
                    );
                  } else if (val < 1) {
                    setValidationErrorText(
                      'Limit can not be set to zero, please change.'
                    );
                  } else if (isNaN(val)) {
                    setValidationErrorText(
                      'Limit shoould be a number, please change.'
                    );
                  } else {
                    setValidationErrorText('');
                    setExpensesAlertLimit(val);
                  }
                }}
              />
              <ApButtonMain disabled={saveDisabled} onClick={handleSave}>
                Save
              </ApButtonMain>
            </InputButtonGroup>
          </RightSection>
        </ContentBlock>
      </CostSectionWrapper>

      {expenseDetails && (
        <ExpenseDetailsModal
          data={{
            projectCodeName,
            userName,
            expenseDetails,
          }}
          isOpen={isCostsModalOpen}
          hasClosed={() => {
            setIsCostsModalOpen(false);
          }}
        />
      )}
    </>
  );
};
